import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import { getCookieConsent, setCookieConsent, resetCookieConsent, initializeAnalytics, initializeMarketing, initializeFunctional } from '../utils/cookieConsent';

const CookieManager = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    functional: false,
    marketing: false
  });

  useEffect(() => {
    if (isOpen) {
      const currentConsent = getCookieConsent();
      if (currentConsent) {
        setPreferences(currentConsent);
      }
    }
  }, [isOpen]);

  const handlePreferenceChange = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleSave = () => {
    setCookieConsent(preferences);
    
    // Initialize features based on selected preferences
    if (preferences.analytics) initializeAnalytics();
    if (preferences.marketing) initializeMarketing();
    if (preferences.functional) initializeFunctional();
    
    onClose();
  };

  const handleReset = () => {
    resetCookieConsent();
    setPreferences({
      essential: true,
      analytics: false,
      functional: false,
      marketing: false
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background rounded-brand shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center">
                <Icon name="Settings" size={20} className="text-secondary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-text-primary">Cookie Preferences</h2>
                <p className="text-sm text-text-secondary">Manage your cookie settings</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              iconName="X"
            />
          </div>

          {/* Cookie Types */}
          <div className="space-y-4 mb-6">
            {/* Essential Cookies */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-brand">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <Icon name="Shield" size={16} className="text-secondary" />
                  <h4 className="font-medium text-text-primary">Essential Cookies</h4>
                </div>
                <p className="text-sm text-text-secondary">
                  These cookies are necessary for the website to function properly. They cannot be disabled.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={preferences.essential}
                  disabled
                  className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                />
                <span className="text-sm text-text-secondary">Always Active</span>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-brand">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <Icon name="BarChart3" size={16} className="text-secondary" />
                  <h4 className="font-medium text-text-primary">Analytics Cookies</h4>
                </div>
                <p className="text-sm text-text-secondary">
                  Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() => handlePreferenceChange('analytics')}
                  className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                />
                <span className="text-sm text-text-secondary">Optional</span>
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-brand">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <Icon name="Settings" size={16} className="text-secondary" />
                  <h4 className="font-medium text-text-primary">Functional Cookies</h4>
                </div>
                <p className="text-sm text-text-secondary">
                  Enable enhanced functionality and personalization, such as remembering your preferences and settings.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={() => handlePreferenceChange('functional')}
                  className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                />
                <span className="text-sm text-text-secondary">Optional</span>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-brand">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <Icon name="Target" size={16} className="text-secondary" />
                  <h4 className="font-medium text-text-primary">Marketing Cookies</h4>
                </div>
                <p className="text-sm text-text-secondary">
                  Used to track visitors across websites to display relevant and engaging advertisements.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={() => handlePreferenceChange('marketing')}
                  className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                />
                <span className="text-sm text-text-secondary">Optional</span>
              </div>
            </div>
          </div>

          {/* Current Status */}
          <div className="bg-muted p-4 rounded-brand mb-6">
            <h4 className="font-medium text-text-primary mb-2">Current Status</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${preferences.analytics ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span className="text-text-secondary">Analytics: {preferences.analytics ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${preferences.functional ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span className="text-text-secondary">Functional: {preferences.functional ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${preferences.marketing ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span className="text-text-secondary">Marketing: {preferences.marketing ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-text-secondary">Essential: Always Enabled</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <div className="flex gap-3">
              <Link to="/cookie-policy">
                <Button
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap"
                >
                  Cookie Policy
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="whitespace-nowrap"
              >
                Reset All
              </Button>
            </div>
            <div className="flex gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="whitespace-nowrap"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="whitespace-nowrap"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieManager;
